import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { useBrand } from "../../../../contexts/BrandContext";

const INITIAL_FORM = {
    event_type: "",
    priority: 0,
    conditions: "",
    actions: "",
    active: true,
};

const CreateRuleDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [eventTypeOptions, setEventTypeOptions] = useState([]);
    const [etLoading, setEtLoading] = useState(false);

    const [actionCatalog, setActionCatalog] = useState([]);
    const [conditionCatalog, setConditionCatalog] = useState([]);
    const [selectedCondition, setSelectedCondition] = useState("");
    const [selectedAction, setSelectedAction] = useState("");

    // Fetch active event types for the current brand when dialog opens
    useEffect(() => {
        if (!open || !brandName) return;
        setEtLoading(true);
        const query = `?brand=${encodeURIComponent(brandName)}&active=true`;
        fetch(`${LOYALTY_API_BASE}/admin/event-types${query}`, {
            headers: { "X-Brand": brandName }
        })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => setEventTypeOptions(Array.isArray(data) ? data : []))
            .catch(() => setEventTypeOptions([]))
            .finally(() => setEtLoading(false));

        // Fetch Catalogs
        fetch(`${LOYALTY_API_BASE}/admin/rule-conditions`, { headers: { "X-Brand": brandName } })
            .then(res => res.ok ? res.json() : { conditions: [] })
            .then(data => setConditionCatalog(data.conditions || []))
            .catch(err => console.error("Failed to load conditions catalog", err));

        fetch(`${LOYALTY_API_BASE}/admin/rule-actions`, { headers: { "X-Brand": brandName } })
            .then(res => res.ok ? res.json() : { actions: [] })
            .then(data => setActionCatalog(data.actions || []))
            .catch(err => console.error("Failed to load actions catalog", err));
    }, [open, brandName]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleInsertCondition = (e) => {
        const type = e.target.value;
        setSelectedCondition(type);
        if (!type) return;
        const conditionConfig = conditionCatalog.find(c => c.type === type);
        let defaultVal = '""';
        if (conditionConfig && conditionConfig.params === "int") defaultVal = 0;
        if (conditionConfig && conditionConfig.params === true) defaultVal = true;
        const template = `{\n  "all": [\n    {\n      "${type}": ${defaultVal}\n    }\n  ]\n}`;
        setForm(p => ({ ...p, conditions: template }));
    };

    const handleInsertAction = (e) => {
        const type = e.target.value;
        setSelectedAction(type);
        if (!type) return;
        const actionConfig = actionCatalog.find(a => a.type === type);
        const example = actionConfig?.examples?.[0] || { type };
        const template = `[\n  ${JSON.stringify(example, null, 2).replace(/\n/g, '\n  ')}\n]`;
        setForm(p => ({ ...p, actions: template }));
    };
    const isValid = brandName && form.event_type;

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            let conditions = null;
            if (form.conditions.trim()) {
                try { conditions = JSON.parse(form.conditions); }
                catch { throw new Error("conditions n'est pas un JSON valide"); }
            }
            let actions = null;
            if (form.actions.trim()) {
                try {
                    actions = JSON.parse(form.actions);
                    if (!Array.isArray(actions)) throw new Error();
                } catch { throw new Error("actions doit être un tableau JSON valide"); }
            }

            const body = {
                brand: brandName,
                event_type: form.event_type,
                priority: Number(form.priority),
                conditions,
                actions,
                active: form.active,
            };
            const res = await fetch(`${LOYALTY_API_BASE}/rules`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    const handleClose = () => { setForm({ ...INITIAL_FORM }); setSelectedCondition(""); setSelectedAction(""); setError(null); onClose(); };

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoFieldSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rules.gradient }}>
                        <GavelIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouvelle Règle
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Brand badge */}
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.rules.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip label={brandName || "Aucune marque"} size="small"
                        sx={{
                            fontWeight: 700, fontSize: 12,
                            bgcolor: brandName ? `${SECTION_COLORS.rules.main}15` : "#fff0f0",
                            color: brandName ? SECTION_COLORS.rules.main : "#DC2626"
                        }} />
                </Box>

                {!brandName && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Veuillez sélectionner une marque avant de créer une règle.
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {/* event_type dropdown */}
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth required select
                            label={etLoading ? "Chargement des types…" : "Type d'événement"}
                            value={form.event_type}
                            onChange={handle("event_type")}
                            size="small"
                            disabled={!brandName || etLoading}
                            helperText={
                                !brandName ? "Sélectionnez une marque d'abord" :
                                    etLoading ? "Récupération en cours…" :
                                        eventTypeOptions.length === 0
                                            ? "⚠️ Aucun type actif — créez-en un dans l'onglet Types d'Événements"
                                            : `${eventTypeOptions.length} type(s) disponible(s)`
                            }
                            sx={fieldSx}
                            SelectProps={{ displayEmpty: true }}
                        >
                            {eventTypeOptions.length === 0 && !etLoading && (
                                <MenuItem value="" disabled>Aucun type d'événement actif</MenuItem>
                            )}
                            {eventTypeOptions.map((et) => (
                                <MenuItem key={et.id} value={et.key}>
                                    <Box>
                                        <Typography fontSize={13} fontWeight={600}>{et.name}</Typography>
                                        <Typography fontSize={11} color="text.secondary">{et.key}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* priority */}
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Priorité" type="number" value={form.priority}
                            onChange={(e) => setForm((p) => ({ ...p, priority: Number(e.target.value) }))}
                            size="small" disabled={!brandName}
                            helperText="Plus élevé = prioritaire"
                            InputProps={{ inputProps: { min: 0 } }} sx={fieldSx} />
                    </Grid>
                    {/* conditions */}
                    <Grid item xs={12}>
                        <TextField fullWidth select size="small" label="Condition" value={selectedCondition}
                            onChange={handleInsertCondition} sx={{ ...fieldSx }}
                            helperText="Sélectionnez la condition applicable. Laissez vide pour exécuter inconditionnellement.">
                            <MenuItem value=""><em>Aucune condition</em></MenuItem>
                            {conditionCatalog.map(c => (
                                <MenuItem key={c.type} value={c.type}>{c.type}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* actions */}
                    <Grid item xs={12}>
                        <TextField fullWidth select size="small" label="Action" value={selectedAction}
                            onChange={handleInsertAction} sx={{ ...fieldSx }}
                            helperText="Sélectionnez l'action à exécuter." required>
                            {actionCatalog.map(a => (
                                <MenuItem key={a.type} value={a.type}>{a.title || a.type}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* active */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">La règle sera appliquée immédiatement</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rules.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rules.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.rules.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.rules.main}40` }
                    }}>
                    {submitting ? "Création..." : "Créer la Règle"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateRuleDialog;
